// @flow

import {useStoreInfo} from '../../../store/utils';
import {useTypedSelector} from '../../../../store';
import {useEffect, useState} from 'react';

import {useBindedActions} from '../../../../utils/globalStore';
import {actions as TaxesActions} from '../duck';

import type {ShoppingCart} from '../../../manual-order/duck';
import {useIsFeatureInTier} from '../../../../utils/component';

import type {TaxType} from '../../../../entities';
import {useStoreCountry} from '../../../country/utils';
import {getTaxTypesService} from '../promises';
import {useToastActions} from '../../../toast-alert/utils';

const actions = {
  fetchProductTaxes: TaxesActions.fetchProductTaxes,
};

export function useFetchProductTaxes() {
  const {fetchProductTaxes} = useBindedActions(actions);
  return fetchProductTaxes;
}

export function useProductsTaxes() {
  return useTypedSelector(state => state.taxesConfig.taxesMap);
}

export function useShoppingCartItemTaxes(shoppingCart: ShoppingCart) {
  const hasTaxesFeature = useIsFeatureInTier('TAXES');
  const store = useStoreInfo();
  const fetchProductTaxes = useFetchProductTaxes();
  const taxesMap = useProductsTaxes();

  useEffect(() => {
    if (!store || !hasTaxesFeature) return;
    if (shoppingCart?.items.length) {
      const items = shoppingCart.items;
      for (let p of items) {
        const productId = p.product.id;
        if (productId && !taxesMap?.[productId]) {
          fetchProductTaxes({storeId: store.id, productId: productId});
        }
      }
    }
  }, [fetchProductTaxes, hasTaxesFeature, shoppingCart, store, taxesMap]);

  return null;
}

export function useTaxesSetting() {
  const hasTaxesFeature = useIsFeatureInTier('TAXES');
  const isFetching = useTypedSelector(
    state => state.taxesConfig.isFetchingTaxSetting
  );
  const taxesSetting = useTypedSelector(state => state.taxesConfig.taxSetting);

  const enabledTaxInclusivePricing: boolean = hasTaxesFeature
    ? !!taxesSetting?.enabledTaxInclusivePricing
    : false;

  return {enabledTaxInclusivePricing, isFetchingTaxSetting: isFetching};
}

export function useStoreTaxTypes() {
  const storeCountry = useStoreCountry();

  const {showToast} = useToastActions();

  const [loading, setLoading] = useState(true);
  const [taxTypes, setTaxTypes] = useState<TaxType[]>([]);

  const countryCode = storeCountry?.code;

  useEffect(() => {
    if (!countryCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getTaxTypesService(countryCode)
      .then(taxTypes => setTaxTypes(taxTypes))
      .catch(() => {
        showToast({
          title: 'Error',
          type: 'danger',
          message: 'Error al obtener los tipos de impuestos',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [showToast, countryCode]);

  return {
    taxTypes,
    loading,
  };
}
